import React from "react";
import {
  Box,
  Center,
  Heading,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../components/UI/CustomButton";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useEffect } from "react";
import { useFormTheme } from "../../hooks/useFormTheme";
import { ClockCountdownIcon } from "@phosphor-icons/react/dist/ssr";
import CustomLoader from "../../components/placeholders/CustomLoader";

const AuthenticationPendingPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, appUserProfile, getUserProfileInfo } = useAuth();
  const cardBg = useColorModeValue("white", "gray.800");
  const outlineColor = useColorModeValue("blue.500", "transparent");
  const btnHeight = useColorModeValue("37px", "42px");
  const { pageBg, darkGradient, lightGradient } = useFormTheme();
  const [isLoading, setIsLoading] = React.useState(true);
  const userStatus = appUserProfile?.status;

  useEffect(() => {
    const checkUserStatus = async () => {
      setIsLoading(true);
      try {
        await getUserProfileInfo();
      } finally {
        setIsLoading(false);
      }
    };

    checkUserStatus();
  }, []);

  useEffect(() => {
    if (!isLoading) {
      if (isAuthenticated && userStatus === "active") {
        navigate("/", { replace: true });
        return;
      }

      if (userStatus === "blocked" || !isAuthenticated) {
        navigate("/login", { replace: true });
      }
    }
  }, [isLoading, isAuthenticated, navigate, userStatus]);

  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      await getUserProfileInfo();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Center
      px={6}
      h="100vh"
      w="full"
      bg={pageBg}
      bgGradient={lightGradient}
      _dark={{
        bgGradient: darkGradient,
      }}
    >
      {isLoading ? (
        <CustomLoader />
      ) : (
        <Box
          maxW="500px"
          w="full"
          borderRadius="xl"
          boxShadow="lg"
          p={{ base: 6, md: 8 }}
          bg={cardBg}
        >
          <VStack spacing={5} align="stretch">
            <Center>
              <ClockCountdownIcon size={77} weight="duotone" />
            </Center>

            <Heading fontSize={{ base: "20px", md: "22px" }} textAlign="center">
              {t("auth.pending.title")}
            </Heading>

            <Text
              textAlign="center"
              color="gray.500"
              _dark={{ color: "gray.300" }}
            >
              {t("auth.pending.description")}
            </Text>

            <CustomButton onClick={() => navigate("/login")}>
              {t("auth.pending.backToLogin")}
            </CustomButton>
            <CustomButton
              outlineColor={outlineColor}
              bgColor="white"
              textColor="blue.500"
              h={btnHeight}
              onClick={handleRefresh}
            >
              {t("auth.pending.refreshPage")}
            </CustomButton>
          </VStack>
        </Box>
      )}
    </Center>
  );
};

export default AuthenticationPendingPage;
